import { useEffect, useState } from 'react';

import {
  IconAlertTriangleFilled,
  IconCircle,
  IconCircleCheck,
  IconDeviceGamepad2,
} from '@tabler/icons-react';
import { IconCircleCheckDisabled, IconCircleDisabled } from 'icons';
import { useTranslation } from 'react-i18next';

import { Typography } from '@cfa/react-components';

import { getPlanStepResourceReferenceName } from '@/utils/stepName';
import { cn } from '@/utils/tailwind';
import { useRouteMatcher } from '@/utils/url';

import Constants from 'constants/index';
import { itemByLanguage } from 'utils/language';

import { PlanAssignment, PlanStep } from '@/_generated/api-clients/xp';

interface PlanItemProcedureProps {
  disabled?: boolean;
  isActive: boolean;
  isComplete: boolean;
  isMissing: boolean;
  language?: string;
  onClick: () => void;
  onCompleteToggle: () => void;
  statuses: PlanAssignment[];
  step: PlanStep;
  userId?: string;
}

// Use literal translation keys to preserve i18next key typing.
const resourceTypeTranslationKeyMap = {
  BRIGHTCOVE: 'resourceTypes.brightcove',
  LEARNUPON: 'resourceTypes.learnupon',
  PROCEDURE: 'resourceTypes.resource',
  JOB_AID: 'resourceTypes.jobAid',
  GAME: 'Generic.game', // Use generic label consistent with existing overrides
  ORG: 'resourceTypes.org',
  QUALITY_PHOTOS: 'resourceTypes.qualityPhotos',
  VIDEO: 'Generic.video', // Use generic label consistent with existing overrides
  PDF: 'resourceTypes.pdf',
  OPERATIONAL_REQUIREMENTS: 'resourceTypes.operationalRequirements',
  PODCAST: 'resourceTypes.podcast',
  SIMULATOR: 'resourceTypes.simulator',
  ELEARNING: 'resourceTypes.eLearning',
} as const satisfies Record<string, string>;

const PlanItemProcedure: React.FC<PlanItemProcedureProps> = ({
  disabled,
  isActive,
  isComplete,
  isMissing,
  language,
  onClick,
  onCompleteToggle,
  statuses,
  step,
  userId,
}) => {
  const { t } = useTranslation();
  const [isCompleted, setIsCompleted] = useState(isComplete);
  const userStatus = statuses
    .find(status => status.userId === userId)
    ?.steps?.find(_ => _.stepId === step.id);
  // Use `duration` (API field) not `stepDuration` (legacy normalized field).
  // AssignedPlanView doesn't transform data through processUserPlanAndAssignments,
  // so stepDuration may not exist. duration is always present from the API.
  // See PATHWAY-2935 for context.
  const stepDuration = userStatus?.duration
    ? Math.round(userStatus.duration / 60)
    : 0;
  const isGame =
    step.resource?.type?.toLocaleLowerCase() ===
    Constants.DOCUMENT_TYPES.GAME.toLocaleLowerCase();
  const isDisabled = isMissing || disabled;

  const baseType = step.resource?.type;
  const resourceTypeKey =
    baseType && baseType in resourceTypeTranslationKeyMap
      ? resourceTypeTranslationKeyMap[
          baseType as keyof typeof resourceTypeTranslationKeyMap
        ]
      : undefined;
  let taskTitle: string = resourceTypeKey
    ? t(resourceTypeKey)
    : (baseType ?? '');

  if (isCompleted && userId) {
    taskTitle = `${taskTitle} - ${t('Generic.completedIn')} ${stepDuration} ${t('Generic.mins')}`;
  }

  const routeMatches = useRouteMatcher();
  const isMyPlansTab = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.TrainingAssignedPath,
  );

  useEffect(() => {
    setIsCompleted(isComplete);
  }, [isComplete]);

  const resolvedLanguage = language ?? 'en';
  const stepName = itemByLanguage(step.name, '', language);
  const stepReferenceName = getPlanStepResourceReferenceName(
    step,
    resolvedLanguage,
  );

  return (
    <>
      <div
        className={cn(
          'flex w-full flex-row items-center gap-2',
          !isDisabled ? 'cursor-pointer' : 'cursor-default',
          isActive && 'pb-2.5',
        )}
      >
        {isCompleted ? (
          <button
            type="button"
            className={cn(
              'mr-2 inline-flex appearance-none border-0 bg-transparent p-0',
              !isDisabled ? 'cursor-pointer' : 'cursor-default',
            )}
            data-testid="StepStatusIcon"
            disabled={isDisabled}
            onClick={() => {
              setIsCompleted(prevState => !prevState);
              onCompleteToggle();
            }}
          >
            {!isDisabled ? (
              <IconCircleCheck className="text-feedback-success-base!" />
            ) : (
              <IconCircleCheckDisabled />
            )}
          </button>
        ) : (
          <button
            type="button"
            className={cn(
              'mr-2 inline-flex appearance-none border-0 bg-transparent p-0',
              !isDisabled ? 'cursor-pointer' : 'cursor-default',
            )}
            data-testid="StepStatusIcon"
            disabled={isDisabled}
            onClick={() => {
              setIsCompleted(prevState => !prevState);
              onCompleteToggle();
            }}
          >
            {!isDisabled ? (
              <IconCircle className="text-grayscale-gray2!" />
            ) : (
              <IconCircleDisabled className="text-grayscale-gray2!" />
            )}
          </button>
        )}
        <button
          type="button"
          className={cn(
            'flex w-full flex-col text-left',
            (isDisabled && !isMyPlansTab) || (isDisabled && isMissing)
              ? 'cursor-default'
              : 'cursor-pointer',
          )}
          data-testid={`PlanStep${step.id}`}
          disabled={isDisabled && (!isMyPlansTab || isMissing)}
          onClick={() => {
            if (step.type === Constants.STEP_TYPES.DOCUMENT) {
              onClick();
            }
          }}
        >
          <Typography data-testid="TaskTitle" variant="overline3">
            {taskTitle}
          </Typography>
          <>
            {isMissing ? (
              <>
                <Typography data-testid="TaskOrProcedureName" variant="body1">
                  {/* remove underscores from name */}
                  {stepName.replace(/_/g, ' ')}
                </Typography>
                <div className="text-feedback-error-base flex items-center">
                  <IconAlertTriangleFilled size={16} />
                  <Typography
                    data-testid="TaskOrProcedureName"
                    variant="body1"
                    className="text-feedback-error-base! ml-2!"
                  >
                    {isMyPlansTab
                      ? t('TrainingPlans.noResource')
                      : t('TrainingPlans.noResourceBuild')}
                  </Typography>
                </div>
              </>
            ) : step.type === Constants.STEP_TYPES.DOCUMENT ? (
              <Typography data-testid="TaskOrProcedureName" variant="body1">
                {stepReferenceName ?? stepName}
              </Typography>
            ) : (
              <Typography data-testid="TaskOrProcedureName" variant="body1">
                {stepName}
              </Typography>
            )}
          </>
        </button>
        {isGame && (
          <div className="text-grayscale-gray2 mx-1.5 w-6 cursor-pointer">
            <IconDeviceGamepad2 className="size-6!" />
          </div>
        )}
      </div>
    </>
  );
};

export default PlanItemProcedure;
